// Console D's bar (56 px, sticky): the sidebar's button, the scope (organization / project /
// app, one popover), the query chips (release:, range:) and, on the right, Find and the agents
// pill. The chips read and write the URL as the old filter bar did (search.ts VIEW_RANGES,
// clampRange): each view takes the ranges it has, and a view that ignores a chip says so.
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation, useNavigate, useSearch } from '@tanstack/react-router'
import { useId } from 'react'
import { appsQuery, meQuery, projectsQuery, windowReleasesQuery } from '../../api/queries'
import { type Translator, useI18n } from '../../i18n'
import { findKey } from '../../lib/keys'
import { sandboxErrorKey, useOpenSandbox } from '../../lib/openSandbox'
import { releasesNewestFirst } from '../../lib/releases'
import { useProjectParams } from '../../routes/params'
import { type FilterKind, SessionFilterChips } from '../../routes/SessionFilterChips'
import {
  carried,
  clampRange,
  RANGE_DAYS,
  type RangeView,
  rangeDays,
  type ScopeBuild,
  type ScopeRange,
  type ScopeSearch,
  type SessionsSearch,
  VIEW_RANGES,
} from '../../routes/search'
import { sandboxFor } from '../../sandbox'
import { Icon } from '../icons'
import { useHiddenProjectName } from '../ProjectDeletions'
import { appLabel } from '../Shell'
import { usePopover } from './usePopover'

/**
 * Whether a view applies the device and OS filters (APS-228): `apply` offers them behind
 * "+ Filter"; `na` shows a carried one dimmed, as not applied here; absent shows none (Sessions
 * has its own chips on the same URL keys).
 */
export type DeviceFiltering = 'apply' | 'na'

interface BarView {
  view: RangeView
  release: boolean
  /** The view's own filters, behind "+ Filter" (a feature flag and its variant, step 3.6). */
  flags?: true
  devices?: DeviceFiltering
}

/** The views that read the chips, by their path in the project, and whether they take a release. */
const VIEWS: readonly (BarView & { path: RegExp })[] = [
  { path: /^\/home$/, view: 'home', release: true, devices: 'apply' },
  { path: /^\/releases$/, view: 'releases', release: true, devices: 'apply' },
  // A release's own page: the path names the release, so the chip has none to take.
  { path: /^\/releases\/[^/]+$/, view: 'releases', release: false, devices: 'na' },
  { path: /^\/issues$/, view: 'issues', release: true, flags: true, devices: 'apply' },
  { path: /^\/sessions$/, view: 'sessions', release: true },
  { path: /^\/logs$/, view: 'logs', release: true, devices: 'na' },
  { path: /^\/performance(\/.*)?$/, view: 'perf', release: true, devices: 'apply' },
  { path: /^\/journeys$/, view: 'perf', release: true, devices: 'na' },
  { path: /^\/dashboards$/, view: 'dashboards', release: false, devices: 'na' },
]

/** Pages without the bar's other chips that still say a carried device filter does not apply. */
const NOT_APPLIED: readonly RegExp[] = [/^\/issues\/[^/]+$/]

/** The chips' view for a path inside a project (`/home`, `/issues`, ...), if it has one. */
export function filterView(path: string): BarView | undefined {
  return VIEWS.find((v) => v.path.test(path))
}

/**
 * Where the build chip (D6, APS-508) counts: every view whose reads take `build`. Shown on all of
 * them, release builds by default, since a filter that hides data must be seen (Mosaic).
 */
const BUILD_APPLIES: readonly RegExp[] = [
  /^\/home$/,
  /^\/health(\/.*)?$/,
  /^\/releases(\/[^/]+)?$/,
  /^\/issues$/,
  /^\/sessions$/,
  /^\/logs$/,
  /^\/performance(\/.*)?$/,
  /^\/explore(\/.*)?$/,
]
/** Pages that show one thing whole (an issue, a session) or that take no build: the chip says so. */
const BUILD_NOT_APPLIED: readonly RegExp[] = [
  /^\/issues\/[^/]+$/,
  /^\/sessions\/[^/]+$/,
  /^\/journeys$/,
  /^\/dashboards$/,
]

/** How a path inside a project treats the build chip (APS-508), if it shows one. */
export function buildFiltering(path: string): DeviceFiltering | undefined {
  if (BUILD_APPLIES.some((p) => p.test(path))) return 'apply'
  return BUILD_NOT_APPLIED.some((p) => p.test(path)) ? 'na' : undefined
}

/** How a path inside a project treats the device and OS filters (APS-228). */
export function deviceFiltering(path: string): DeviceFiltering | undefined {
  return filterView(path)?.devices ?? (NOT_APPLIED.some((p) => p.test(path)) ? 'na' : undefined)
}

/** "last hour", "last 24 h", "last 7 days". */
export function rangeLabel(t: Translator['t'], range: ScopeRange): string {
  const days = RANGE_DAYS[range]
  return days < 1
    ? t('filters.range.hour')
    : days === 1
      ? t('issues.chip.day')
      : t('issues.chip.days', { days })
}

export function Bar({
  onMenu,
  onFind,
  onRail,
  drawerOpen,
  rail,
  erasing = false,
}: {
  onMenu: () => void
  onFind: () => void
  onRail: () => void
  drawerOpen: boolean
  /** The sidebar is folded to its rail. */
  rail: boolean
  /** The project is being deleted (APS-406): the chips filter nothing, so they go. */
  erasing?: boolean
}) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  return (
    <header className="bar">
      <button
        type="button"
        className="btn ghost sm bar-menu"
        aria-label={t('shell.menu')}
        aria-expanded={drawerOpen}
        aria-controls="nav"
        onClick={onMenu}
      >
        <Icon name="menu" />
      </button>
      <RailButton rail={rail} onRail={onRail} />
      <Scope />
      {erasing ? null : <Chips />}
      <div className="bar-r">
        <button type="button" className="search" aria-haspopup="dialog" onClick={onFind}>
          <Icon name="search" />
          <span className="lbl">{t('shell.find')}</span>
          <kbd translate="no">{findKey()}</kbd>
        </button>
        {/* In the sandbox too: its Agents page gives a token for the CLI and MCP (D105). */}
        <Link
          className="agents"
          to="/o/$orgId/p/$projectId/agents"
          params={{ orgId, projectId }}
          title={t('shell.agentsTitle')}
        >
          <Icon name="agent" small />
          <span className="lbl">{t('nav.agents')}</span>
        </Link>
      </div>
    </header>
  )
}

/** The bar's button that folds the sidebar to a rail of icons (`[`), on a wide screen. */
function RailButton({ rail, onRail }: { rail: boolean; onRail: () => void }) {
  const { t } = useI18n()
  return (
    <button
      type="button"
      className="btn ghost sm bar-side"
      aria-controls="nav"
      aria-expanded={!rail}
      onClick={onRail}
      title={rail ? t('shell.railOpenTitle') : t('shell.railTitle')}
    >
      <Icon name="sidebar" />
      <span className="vh">{rail ? t('shell.railOpen') : t('shell.rail')}</span>
    </button>
  )
}

/** Organization / project / app, and the popover that changes them. */
function Scope() {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as ScopeSearch
  const sandbox = sandboxFor(projectId)
  const me = useQuery({ ...meQuery, enabled: !sandbox })
  const projects = useQuery({ ...projectsQuery(orgId), enabled: !sandbox })
  const apps = useQuery(appsQuery(orgId, projectId))
  const pop = usePopover('below')
  const popId = useId()
  const openSandbox = useOpenSandbox()
  const org = me.data?.orgs.find((o) => o.id === orgId)
  const project = projects.data?.find((p) => p.id === projectId)
  const app = apps.data?.find((a) => a.id === search.app)
  const orgName = sandbox ? null : (org?.name ?? '')
  // A project being deleted is in no list: its name comes with its deletion (APS-401).
  const hidden = useHiddenProjectName(orgId, projectId, !!sandbox || !projects.data || !!project)
  const projectName = sandbox ? sandbox.projectName : (project?.name ?? hidden ?? '')
  const setApp = (appId: string | undefined) => {
    pop.close()
    navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, app: appId, release: undefined }),
    })
  }
  return (
    <>
      <button
        ref={pop.anchor}
        type="button"
        className="scope"
        aria-expanded={pop.open}
        aria-controls={popId}
        aria-label={t('shell.scopeLabel', {
          scope: [orgName, projectName, app ? appLabel(app) : t('shell.allAppsShort')]
            .filter(Boolean)
            .join(' / '),
        })}
        onClick={pop.toggle}
      >
        {orgName ? (
          <span className="scope-org">
            <span>{orgName}</span>
            <span className="sep" aria-hidden="true">
              /
            </span>
          </span>
        ) : null}
        <b className="scope-p">{projectName}</b>
        <span className="sep" aria-hidden="true">
          /
        </span>
        <b className="scope-a">{app ? appLabel(app) : t('shell.allAppsShort')}</b>
        <Icon name="chev" small />
      </button>
      {pop.open ? (
        <div ref={pop.pop} id={popId} className="pop scope-pop" style={pop.style}>
          <p className="pal-g">{orgName ?? projectName}</p>
          {(sandbox ? [{ id: projectId, name: projectName }] : (projects.data ?? [])).map((p) =>
            p.id === projectId ? (
              <div key={p.id}>
                <Link
                  className="pal-i on"
                  to="/o/$orgId/p/$projectId/home"
                  params={{ orgId, projectId }}
                  search={carried(search)}
                  onClick={() => pop.close()}
                  aria-current="true"
                >
                  <span />
                  <span>{p.name}</span>
                </Link>
                {(apps.data ?? []).map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={a.id === search.app ? 'pal-i child on' : 'pal-i child'}
                    aria-pressed={a.id === search.app}
                    onClick={() => setApp(a.id)}
                  >
                    <span />
                    <span translate="no">{`${appLabel(a)} · ${a.bundle_id}`}</span>
                  </button>
                ))}
                <button
                  type="button"
                  className={search.app ? 'pal-i child' : 'pal-i child on'}
                  aria-pressed={!search.app}
                  onClick={() => setApp(undefined)}
                >
                  <span />
                  <span>{t('shell.allAppsIn', { project: p.name })}</span>
                </button>
              </div>
            ) : (
              <Link
                key={p.id}
                className="pal-i"
                to="/o/$orgId/p/$projectId/home"
                params={{ orgId, projectId: p.id }}
                onClick={() => pop.close()}
              >
                <span />
                <span>{p.name}</span>
              </Link>
            ),
          )}
          {sandbox ? null : (
            <>
              <hr />
              <Link
                className="pal-i"
                to="/o/$orgId/new-project"
                params={{ orgId }}
                onClick={() => pop.close()}
              >
                <Icon name="plus" />
                <span>{t('shell.newProject')}</span>
              </Link>
              {/* The sandbox opens from inside the console only, for signed-in people (D105). */}
              <button
                type="button"
                className="pal-i"
                disabled={openSandbox.pending}
                onClick={() => openSandbox.open()}
              >
                <Icon name="play" />
                <span>{t('sandbox.explore')}</span>
              </button>
              {openSandbox.error ? (
                <p
                  role="alert"
                  className="notice error"
                  style={{ margin: '6px 4px', fontSize: 13 }}
                >
                  {t(sandboxErrorKey(openSandbox.error))}
                </p>
              ) : null}
            </>
          )}
          {me.data && me.data.orgs.length > 1 ? (
            <>
              <p className="pal-g">{t('shell.switchOrg')}</p>
              {me.data.orgs.map((o) => (
                <Link
                  key={o.id}
                  className={o.id === orgId ? 'pal-i on' : 'pal-i'}
                  to="/o/$orgId"
                  params={{ orgId: o.id }}
                  onClick={() => pop.close()}
                >
                  <Icon name="org" />
                  <span>{o.name}</span>
                </Link>
              ))}
            </>
          ) : null}
        </div>
      ) : null}
    </>
  )
}

/** The release: and range: chips, for the views that read them. */
export function Chips() {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const location = useLocation()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as ScopeSearch
  const base = `/o/${orgId}/p/${projectId}`
  const path = location.pathname.startsWith(base) ? location.pathname.slice(base.length) : ''
  const at = path ? filterView(path) : undefined
  const devices = path ? deviceFiltering(path) : undefined
  const now = Math.floor(Date.now() / 60_000) * 60_000
  // The views' own release lists use the same query: one request for both.
  const releases = useQuery({
    ...windowReleasesQuery(projectId, 30, now),
    enabled: !!at?.release,
  })
  const builds = path ? buildFiltering(path) : undefined
  if (!at)
    return (devices === 'na' && (search.device || search.os)) || builds ? (
      <>
        <span className="bar-div" aria-hidden="true" />
        <section className="chips" aria-label={t('filters.label')}>
          {builds ? <BuildChip applies={builds === 'apply'} /> : null}
          {devices === 'na' && (search.device || search.os) ? (
            <ScopeFilters devices="na" flags={false} days={30} compact />
          ) : null}
        </section>
      </>
    ) : null
  const set = (patch: ScopeSearch) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }) })
  // The app's releases, or every app's when none is in scope, newest first.
  const names = [
    ...new Set(
      releases.data ? releasesNewestFirst(releases.data, search.app).map((r) => r.release) : [],
    ),
  ]
  // A release from a link that is not among the last 30 days' still shows, as chosen.
  if (search.release && !names.includes(search.release)) names.unshift(search.release)
  const { ranges } = VIEW_RANGES[at.view]
  const range = clampRange(search.range, at.view)
  const noRelease =
    at.view === 'releases' ? t('filters.release.latest') : t('issues.chip.allReleases')
  return (
    <>
      <span className="bar-div" aria-hidden="true" />
      <section className="chips" aria-label={t('filters.label')}>
        {at.release || search.release ? (
          <PickChip
            name={t('shell.chip.release')}
            value={search.release ?? noRelease}
            ignored={at.release ? undefined : t('shell.chip.ignored')}
            options={
              at.release
                ? [{ value: '', label: noRelease }, ...names.map((r) => ({ value: r, label: r }))]
                : []
            }
            current={search.release ?? ''}
            onPick={(v) => set({ release: v || undefined })}
            clear={search.release ? () => set({ release: undefined }) : undefined}
            clearLabel={t('shell.chip.clearRelease')}
          />
        ) : null}
        <PickChip
          name={t('shell.chip.range')}
          value={rangeLabel(t, range)}
          ignored={
            ranges.length > 1
              ? undefined
              : t('filters.range.fixed', { range: rangeLabel(t, range) })
          }
          options={
            ranges.length > 1 ? ranges.map((r) => ({ value: r, label: rangeLabel(t, r) })) : []
          }
          current={range}
          onPick={(v) => set({ range: v as ScopeRange })}
          clear={search.range && ranges.length > 1 ? () => set({ range: undefined }) : undefined}
          clearLabel={t('shell.chip.clearRange')}
        />
        {builds ? <BuildChip applies={builds === 'apply'} /> : null}
        {at.devices || at.flags ? (
          <ScopeFilters
            devices={at.devices}
            flags={!!at.flags}
            days={rangeDays(search.range, at.view)}
            compact
          />
        ) : null}
      </section>
    </>
  )
}

/**
 * The bar's filters under the page head (PageHeadRow), for a window too narrow for the bar's
 * chips (styles/shell.css shows it at 960 px and below): the same "+ Filter" and set chips, in
 * one row that scrolls sideways. Nothing on a view without them.
 */
export function ScopeFilterRow() {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const location = useLocation()
  const search = useSearch({ strict: false }) as ScopeSearch
  const base = `/o/${orgId}/p/${projectId}`
  const path = location.pathname.startsWith(base) ? location.pathname.slice(base.length) : ''
  const at = path ? filterView(path) : undefined
  const devices = path ? deviceFiltering(path) : undefined
  const builds = path ? buildFiltering(path) : undefined
  const carriedOnly = devices === 'na' && !at?.flags
  const filters =
    (devices || at?.flags) && !(carriedOnly && !search.device && !search.os) ? (
      <ScopeFilters
        devices={devices}
        flags={!!at?.flags}
        days={at ? rangeDays(search.range, at.view) : 30}
      />
    ) : null
  if (!filters && !builds) return null
  return (
    <section className="pg-scope" aria-label={t('filters.label')}>
      {builds ? <BuildChip applies={builds === 'apply'} /> : null}
      {filters}
    </section>
  )
}

/**
 * The bar's "+ Filter" (APS-228, the mock's dashed chip): device and OS on every view that
 * applies them, and a feature flag and its variant on Issues (step 3.6), in the two-step popover
 * Sessions' chips use (APS-400), with their session counts in the scope. A set filter reads
 * `device: iPhone17,1 ×`; on a view that does not apply them (`na`) a carried one shows dimmed,
 * still clearable, and nothing can be added. Values are the app's: shown as text, never
 * translated.
 */
export function ScopeFilters({
  devices,
  flags,
  days,
  compact = false,
}: {
  devices: DeviceFiltering | undefined
  flags: boolean
  days: number
  /** The bar's one chip, `+ Filter` or `Filters · N` (Mosaic, APS-228); the full chips if not. */
  compact?: boolean
}) {
  const { t } = useI18n()
  const { projectId } = useProjectParams()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as SessionsSearch
  const set = (patch: Partial<SessionsSearch>) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }) })
  const offered: FilterKind[] = [
    ...(devices === 'apply' ? (['device', 'os'] as const) : []),
    ...(flags ? (['flag'] as const) : []),
  ]
  return (
    <>
      {offered.length ? (
        <SessionFilterChips
          projectId={projectId}
          search={search}
          filters={{
            app_id: search.app,
            release: search.release,
            device_model: search.device,
            os_version: search.os,
            days,
          }}
          onChange={set}
          kinds={offered}
          addLabel={t('shell.chip.addFilter')}
          compact={compact ? { label: t('shell.chip.filters') } : undefined}
        />
      ) : null}
      {devices === 'na' ? (
        <SessionFilterChips
          projectId={projectId}
          search={search}
          filters={{ days }}
          onChange={set}
          kinds={['device', 'os']}
          inert={t('shell.chip.notApplied')}
          compact={compact ? { label: t('shell.chip.filters') } : undefined}
        />
      ) : null}
    </>
  )
}

/**
 * Which builds the view counts (D6, APS-508): `build: release` by default, with no ×, as
 * `release: all`; `build: debug` or `build: all` when chosen, × back to release (Mosaic).
 */
function BuildChip({ applies }: { applies: boolean }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as ScopeSearch
  const set = (build: ScopeBuild | undefined) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, build }) })
  const value: Record<'release' | ScopeBuild, string> = {
    release: t('shell.chip.build.release'),
    debug: t('shell.chip.build.debug'),
    all: t('shell.chip.build.all'),
  }
  return (
    <PickChip
      name={t('shell.chip.build')}
      value={value[search.build ?? 'release']}
      ignored={applies ? undefined : t('shell.chip.notApplied')}
      options={
        applies
          ? [
              { value: '', label: t('shell.chip.build.option.release') },
              { value: 'debug', label: t('shell.chip.build.option.debug') },
              { value: 'all', label: t('shell.chip.build.option.all') },
            ]
          : []
      }
      current={search.build ?? ''}
      onPick={(v) => set(v ? (v as ScopeBuild) : undefined)}
      clear={search.build ? () => set(undefined) : undefined}
      clearLabel={t('shell.chip.clearBuild')}
      hint={t('shell.chip.build.hint')}
      words
    />
  )
}

/** A chip that opens a small menu of values; `×` clears it back to the view's default. */
function PickChip({
  add,
  name,
  value,
  options,
  current,
  onPick,
  clear,
  clearLabel,
  ignored,
  hint,
  words = false,
}: {
  name: string
  value: string
  options: { value: string; label: string }[]
  current: string
  onPick: (value: string) => void
  clear: (() => void) | undefined
  clearLabel: string
  /** Why this view does not change with the chip (its tooltip), when it does not. */
  ignored: string | undefined
  /** A dashed "+ Filter" chip that opens the choices instead of showing a value. */
  add?: string
  /** A line under the choices that says what they mean. */
  hint?: string
  /** The value and choices are the console's own words (translatable), not data (a release). */
  words?: boolean
}) {
  const pop = usePopover('below')
  const popId = useId()
  // A chip the page does not use is dashed and says why on click (D121), not only in a tooltip.
  const why = !options.length && ignored ? ignored : undefined
  const opens = options.length > 0 || !!why
  return (
    <span
      className={['chip pick', add ? 'add' : '', why ? 'off' : ''].join(' ').trim()}
      title={ignored}
    >
      <button
        ref={pop.anchor}
        type="button"
        className="chip-open"
        aria-expanded={opens ? pop.open : undefined}
        aria-controls={opens ? popId : undefined}
        aria-disabled={opens ? undefined : true}
        aria-label={add ? `${add}: ${name}` : undefined}
        aria-description={why}
        onClick={() => opens && pop.toggle()}
      >
        {add ? (
          <span aria-hidden="true">{`+ ${add}`}</span>
        ) : (
          <>
            <span>{name}</span>
            <span className="v" translate={words ? undefined : 'no'}>
              {value}
            </span>
          </>
        )}
      </button>
      {clear ? (
        <button type="button" aria-label={clearLabel} onClick={clear}>
          <Icon name="x" small />
        </button>
      ) : null}
      {pop.open ? (
        <div ref={pop.pop} id={popId} className="pop chip-pop" style={pop.style}>
          {why ? <p className="chip-why">{why}</p> : null}
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              className={o.value === current ? 'pal-i on' : 'pal-i'}
              aria-pressed={o.value === current}
              onClick={() => {
                pop.close(true)
                onPick(o.value)
              }}
            >
              <span />
              <span translate={words ? undefined : 'no'}>{o.label}</span>
            </button>
          ))}
          {hint && !why ? <p className="chip-hint">{hint}</p> : null}
        </div>
      ) : null}
    </span>
  )
}
